import type {
  AnchorHTMLAttributes,
  ComponentProps,
  ElementRef,
  HTMLAttributes,
} from "react";
import { forwardRef } from "react";

import { tw } from "@/shared/utils";

const Root = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={tw(
        "relative flex flex-col gap-2 rounded-lg border border-secondary-200 bg-white p-4 text-neutral-950",
        className,
      )}
      {...props}
    />
  ),
);
Root.displayName = "RequestCard.Root";

const Title = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={tw("font-semibold text-neutral-950", className)}
    {...props}
  >
    {children}
  </h3>
));
Title.displayName = "RequestCard.Title";

const Description = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={tw("text-neutral-900", className)} {...props} />
));
Description.displayName = "RequestCard.Description";

const Details = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={tw(
        "flex flex-col gap-1.5 rounded-lg border border-neutral-600 p-3 md:w-fit md:flex-row md:px-4",
        className,
      )}
      {...props}
    />
  ),
);
Details.displayName = "RequestCard.Details";

const Label = forwardRef<ElementRef<"label">, ComponentProps<"label">>(
  ({ className, htmlFor, ...props }, ref) => (
    <label
      htmlFor={htmlFor}
      ref={ref}
      className={tw("text-sm text-neutral-700", className)}
      {...props}
    />
  ),
);
Label.displayName = "RequestCard.Label";

const Link = forwardRef<
  HTMLAnchorElement,
  AnchorHTMLAttributes<HTMLAnchorElement>
>(({ className, children, ...props }, ref) => (
  <a
    ref={ref}
    target="_blank"
    rel="noreferrer"
    className={tw(
      "inline-flex w-fit items-center gap-1 text-sm text-primary-950 underline underline-offset-2",
      className,
    )}
    {...props}
  >
    {children}
  </a>
));
Link.displayName = "RequestCard.Link";

const Footer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={tw("flex items-center", className)} {...props} />
  ),
);
Footer.displayName = "RequestCard.Footer";

export const RequestCard = {
  Root,
  Footer,
  Title,
  Description,
  Details,
  Label,
  Link,
};
